// src/pages/accident/accident-upload.tsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import "../../../../helper/camera/ClCamera.css";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import Header, { HeaderType } from "../../../../components/Header";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import PublishIcon from "@mui/icons-material/Publish";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadIcon from "@mui/icons-material/Upload";

import { environment } from "../../../../environment/environment";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { getAccessToken, useAuth } from "@/context/auth";

import {
  CONSTRAINTS_1080P,
  normalizeForUpload,
  takeStillPhotoFromTrack,
  scaleToMax1080,
  upgradeVideoTrackQuality,
} from "../../../../helper/camera/webcam-utils";

import {
  BuildBackUrl,
  EnsureNavigationLoaded,
  SetLocation,
  GetNavigationLength,
} from "@/helper/navigation";
import { useEffect } from "react";

interface UploadsProps {
  style?: any;
  loading: boolean;
  offline: boolean;
}

type Stage = "idle" | "camera" | "preview";

const SingleUploads: React.FC<UploadsProps> = (props) => {
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const tpath = (p: string) => {
    const clean = p.startsWith("/") ? p.slice(1) : p;
    return tenant ? `/${tenant}/${clean}` : `/${clean}`;
  };

  const [sp] = useSearchParams();
  const confirm = useConfirm();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";

  const [stage, setStage] = useState<Stage>("idle");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const webcamRef = useRef<Webcam | null>(null);

  const stepFromUrl = Number(sp.get("step") ?? "0");

  // Hidden inputs: gallery vs native camera
  const filePickerRef = useRef<HTMLInputElement | null>(null);
  const cameraPickerRef = useRef<HTMLInputElement | null>(null);

  const isMobile = useMemo(
    () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    [],
  );
  const [useNativeCamera, setUseNativeCamera] = useState<boolean>(isMobile);

  const returnParam = sp.get("return");
  const isRequired = sp.get("required") === "1";
  const returnUrl = useMemo(() => {
    if (!returnParam) return null;
    try {
      const decoded = decodeURIComponent(returnParam);
      // normalize to a proper path for navigate()
      return decoded.startsWith("/") ? decoded : `/${decoded}`;
    } catch {
      return null;
    }
  }, [returnParam]);

  const goBackToCallerOrFlow = async () => {
    if (returnUrl) {
      navigate(returnUrl, { replace: true });
      return;
    }
    if (returnToFinal && finalUrl) {
      navigate(tpath(finalUrl), { replace: true });
      return;
    }

    await routeBack();
  };
  const finalUrl = sessionStorage.getItem("finalReturnUrl");
  const returnToFinal = sessionStorage.getItem("returnToFinal") === "1";
  const hasExplicitReturn = !!returnUrl;

  // We only want wizard-nav behavior if we DON'T have a return target.
  const shouldUseWizardNav = !hasExplicitReturn && !(returnToFinal && finalUrl);

  const showConfirmDialog = (title: string, message: string) =>
    confirm({ catchOnCancel: false, title, description: message });

  // Native pickers
  const openDevicePicker = () => filePickerRef.current?.click();
  const openNativeCameraPicker = () => cameraPickerRef.current?.click();

  const onPickedFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    const scaled1080p = await scaleToMax1080(dataUrl);
    setCapturedImage(scaled1080p);
    setStage("preview");
    e.target.value = "";
  };

  // Desktop webcam events
  const handleUserMediaError = useCallback(() => {
    setUseNativeCamera(true);
    openNativeCameraPicker();
  }, []);

  const handleUserMediaOk = useCallback(() => {
    setUseNativeCamera(false);
    upgradeVideoTrackQuality(webcamRef);
  }, []);

  // Actions
  const startCamera = () => {
    setUseNativeCamera(false);
    setStage("camera");
  };
  const startUploadFromDevice = () => openDevicePicker();
  const startOpenNativeCamera = () => openNativeCameraPicker();

  const captureFromCamera = async () => {
    if (useNativeCamera || !webcamRef.current) {
      openNativeCameraPicker();
      return;
    }
    let dataUrl = await takeStillPhotoFromTrack(webcamRef);
    if (!dataUrl) {
      dataUrl = (webcamRef.current as any)?.getScreenshot({
        width: 1920,
        height: 1080,
      }) as string | undefined;
    }
    if (!dataUrl) {
      setUseNativeCamera(true);
      openNativeCameraPicker();
      return;
    }
    const scaled1080p = await scaleToMax1080(dataUrl);
    setCapturedImage(scaled1080p);
    setStage("preview");
  };

  const discardImage = () => {
    setCapturedImage(null);
    setStage("idle");
  };

  const routeBack = async () => {
    const idx = Number(sp.get("step") ?? "0");
    if (Number.isFinite(idx) && idx >= 0) SetLocation(idx);

    if (GetNavigationLength() === 0) {
      const ok = await EnsureNavigationLoaded(authTokens as any);
      if (ok) SetLocation(idx);
    }

    const prevUrl = BuildBackUrl(stepFromUrl);
    navigate(tpath(prevUrl), { replace: true });
  };

  useEffect(() => {
    if (!shouldUseWizardNav) return;

    (async () => {
      if (Number.isFinite(stepFromUrl) && stepFromUrl >= 0) {
        SetLocation(stepFromUrl);
      }

      if (GetNavigationLength() === 0) {
        const ok = await EnsureNavigationLoaded(authTokens as any);
        if (ok) SetLocation(stepFromUrl);
      }
    })();
  }, [shouldUseWizardNav, stepFromUrl, authTokens]);

  const uploadImage = async () => {
    try {
      if (!capturedImage) {
        showConfirmDialog(
          "Missing image",
          "Please capture or pick a photo first.",
        );
        return;
      }
      setLoading(true);

      if (props.offline) {
        const key = `digialert_pwa_${Math.random().toString(36).slice(2, 7)}`;
        localStorage.setItem(key, capturedImage);
        showConfirmDialog(
          "Image saved locally!",
          "It appears you are offline. We cached your image and will upload once the connection is detected.",
        );
        discardImage();
        setLoading(false);
        return;
      }

      const rawLinked = window.localStorage.getItem("linkedDocumentID") || "";
      const questionAnswerID: string = rawLinked
        ? String(JSON.parse(rawLinked))
        : "";

      const { base64, type } = await normalizeForUpload(capturedImage);
      const documents = {
        linkedId: questionAnswerID,
        fileName: `digialert_pwa_${questionAnswerID}.${type}`,
        file: base64,
        fileType: type,
        documentTypeId: "00000000-0000-0000-0000-000000000004",
        formId: null,
      };

      const config = { headers: { Authorization: `Bearer ${token}` } };
      setUploading(true);

      const resp = await axios.post(
        `${environment.baseUrl}/document`,
        documents,
        config,
      );
      checkUploadStatus(resp);
    } catch (err) {
      setUploading(false);
      setLoading(false);
      showConfirmDialog(
        "Failed to upload image!",
        "We were unable to upload your image.",
      );
      if (err instanceof Error) console.error(err.message);
    }
  };

  const checkUploadStatus = (data: any) => {
    setUploading(false);
    if (data.status >= 200 && data.status < 300) {
      setLoading(false);
      const docCount = JSON.parse(localStorage.getItem("documentCount") || "0");
      const newCount = (parseInt(docCount, 10) || 0) + 1;
      localStorage.setItem("documentCount", JSON.stringify(newCount));
      const previewField = localStorage.getItem("activeCameraPreviewField") || "";
      if (previewField && capturedImage) {
        localStorage.setItem(`cameraPreview:${previewField}`, capturedImage);
      }
      showConfirmDialog("Success!", "Image uploaded to Media Library.").then(
        () => {
          discardImage();
          goBackToCallerOrFlow();
        },
      );
    } else {
      setLoading(false);
      showConfirmDialog(
        "Failed to upload image!",
        "We were unable to upload your image.",
      );
    }
  };

  // Button groups (exact same look you’re using)
  const ButtonsIdle = (
    <Grid container spacing={1}>
      <Grid size={12 as any}>
        <Button
          variant="outlined"
          color="primary"
          className="captureButton"
          onClick={startUploadFromDevice}
          startIcon={<UploadIcon />}
          fullWidth
          size="large"
        >
          Upload from device
        </Button>
      </Grid>
      <Grid size={12 as any}>
        <Button
          variant="outlined"
          color="secondary"
          className="captureButton"
          onClick={isMobile ? startOpenNativeCamera : startCamera}
          startIcon={<CameraAltIcon />}
          fullWidth
          size="large"
        >
          {isMobile ? "Open camera" : "Open camera (desktop)"}
        </Button>
      </Grid>
    </Grid>
  );

  const ButtonsCamera = (
    <Grid container spacing={1}>
      <Grid size={12 as any}>
        <Button
          variant="contained"
          color="secondary"
          className="captureButton"
          onClick={captureFromCamera}
          startIcon={<CameraAltIcon />}
          fullWidth
          size="large"
        >
          Capture Image
        </Button>
      </Grid>
      <Grid size={12 as any}>
        <Button
          variant="outlined"
          color="primary"
          className="deleteButton"
          onClick={() => setStage("idle")}
          fullWidth
          size="large"
        >
          Cancel
        </Button>
      </Grid>
    </Grid>
  );

  const ButtonsPreview = (
    <Grid size={12 as any}>
      <Button
        variant="contained"
        color="primary"
        className="captureButton"
        onClick={uploadImage}
        disabled={loading}
        startIcon={<PublishIcon />}
        fullWidth
        size="large"
      >
        Accept
      </Button>
      <Button
        variant="contained"
        color="secondary"
        className="deleteButton"
        onClick={discardImage}
        startIcon={<DeleteIcon />}
        fullWidth
        disabled={loading}
        size="large"
        style={{ marginTop: 8 }}
      >
        Recapture
      </Button>
    </Grid>
  );

  return (
    <>
      <Header type={HeaderType.AccidentSupport} />
      <Container maxWidth="sm">
        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={1}>
              <Grid size={12 as any}>
                <Grid container justifyContent="center">
                  <Typography variant="caption">
                    Capture the relevant images
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={1}>
                <Grid size={12 as any}>
                  <Grid container justifyContent="center">
                    {/* Desktop live camera (never on mobile) */}
                    {!isMobile && stage === "camera" && (
                      <div className="imageStage">
                        <Webcam
                          ref={webcamRef}
                          audio={false}
                          mirrored={false}
                          playsInline
                          screenshotFormat="image/jpeg"
                          screenshotQuality={1}
                          videoConstraints={CONSTRAINTS_1080P}
                          onUserMedia={handleUserMediaOk}
                          onUserMediaError={handleUserMediaError}
                          className="cameraVideo"
                        />
                      </div>
                    )}

                    {/* Preview of selected/captured image */}
                    {stage === "preview" && capturedImage && (
                      <div className="imageStage">
                        <img
                          src={capturedImage}
                          alt="captured"
                          className="cameraPreview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}

                    {/* Hidden inputs for native pickers */}
                    <input
                      ref={filePickerRef}
                      type="file"
                      accept="image/*"
                      multiple={false}
                      style={{ display: "none" }}
                      onChange={onPickedFile}
                    />
                    <input
                      ref={cameraPickerRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple={false}
                      style={{ display: "none" }}
                      onChange={onPickedFile}
                    />
                  </Grid>
                </Grid>

                <Grid size={12 as any}>
                  <Grid container justifyContent="center">
                    {stage === "idle" && ButtonsIdle}
                    {stage === "idle" && isRequired && (
                      <Typography sx={{ mt: 1 }} color="error" variant="body2">
                        This step is required. Please capture or upload at least one photo to continue.
                      </Typography>
                    )}
                    {stage === "camera" && ButtonsCamera}
                    {stage === "preview" && ButtonsPreview}
                  </Grid>
                </Grid>

                <Grid size={12 as any}>
                  <Grid container justifyContent="center">
                    <Button
                      onClick={() => void goBackToCallerOrFlow()}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      disabled={loading}
                      size="large"
                      style={{ marginTop: 16 }}
                    >
                      <strong>Back</strong>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
      <BrandLoader open={loading} />
    </>
  );
};

export default SingleUploads;
